import React from 'react';
import { logger } from '../../../../../src/logger';
import { ChartData, ChartProps, BarLineChartProps, BarLineChartType, PieChartData } from '../types/interfaces';
import { EmptyStateMessage } from '../../../fintech-ops/EmptyStateMessage';
import { CHART_TYPES, CHART_DEFAULTS } from '../constants';

// Generic validation types
interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

interface ValidationRule<T> {
  validate: (data: T) => boolean;
  errorMessage: string;
}

// Generic chart validation system
const createChartValidator = <T extends unknown>(rules: ValidationRule<T>[]) => {
  return (data: T): ValidationResult => {
    const errors: string[] = [];
    rules.forEach((rule) => {
      if (!rule.validate(data)) {
        errors.push(rule.errorMessage);
      }
    });
    return {
      isValid: errors.length === 0,
      errors
    };
  };
};

// Common validation rules
const commonChartRules: ValidationRule<Record<string, unknown>>[] = [
  {
    validate: (chart) => Boolean(chart && typeof chart === 'object'),
    errorMessage: 'Chart must be a valid object'
  },
  {
    validate: (chart) => Boolean(chart.chartType),
    errorMessage: 'Chart must have a chartType'
  }
];

// Pie chart specific validation rules
const pieChartRules: ValidationRule<Record<string, unknown>>[] = [
  {
    validate: (chart) => {
      const data = chart.data as unknown[];
      return (
        Array.isArray(data) &&
        data.length > 0 &&
        data.every(
          (item) =>
            item &&
            typeof item === 'object' &&
            (('title' in item && typeof item.title === 'string') ||
              ('name' in item && typeof item.name === 'string') ||
              ('label' in item && typeof item.label === 'string')) &&
            'value' in item &&
            typeof item.value === 'number'
        )
      );
    },
    errorMessage: 'Pie chart must have valid data array with title/name/label and value properties'
  }
];

// Bar/Line chart specific validation rules
const barLineChartRules: ValidationRule<Record<string, unknown>>[] = [
  {
    validate: (chart) => Boolean(chart.xAxis && chart.yAxis),
    errorMessage: 'Bar/Line chart must have xAxis and yAxis'
  },
  {
    validate: (chart) => Array.isArray(chart.xAxis) || (typeof chart.xAxis === 'object' && chart.xAxis !== null),
    errorMessage: 'xAxis must be an array or object'
  },
  {
    validate: (chart) => {
      const yAxis = chart.yAxis;
      return Array.isArray(yAxis) || (typeof yAxis === 'object' && yAxis !== null && Object.values(yAxis).every((series) => Array.isArray(series)));
    },
    errorMessage: 'yAxis must be an array or object with array values'
  }
];

// Enhanced validation function that maintains backward compatibility
const isValidChart = (chart: unknown): boolean => {
  if (!chart || typeof chart !== 'object') return false;
  const c = chart as Record<string, unknown>;

  const commonValidation = createChartValidator(commonChartRules)(c);
  if (!commonValidation.isValid) return false;

  if (c.chartType === CHART_TYPES.PIE) {
    return createChartValidator(pieChartRules)(c).isValid;
  }

  if (c.chartType === CHART_TYPES.LINE || c.chartType === CHART_TYPES.BAR) {
    return createChartValidator(barLineChartRules)(c).isValid;
  }

  return true;
};

// Generic data transformation interface
interface DataTransformer<TInput, TOutput> {
  transform: (data: TInput) => TOutput;
  validate?: (data: TInput) => ValidationResult;
}

// Specialized transformer that can return null
interface NullableDataTransformer<TInput, TOutput> extends Omit<DataTransformer<TInput, TOutput>, 'transform'> {
  transform: (data: TInput) => TOutput | null;
}

// Parse chart data with enhanced error handling
export const parseChartData = (responseMessage: string): ChartData[] => {
  try {
    const jsonMatch = responseMessage.match(/```json([\s\S]*?)(?:```|$)/);
    if (!jsonMatch) {
      throw new Error('No valid JSON data found in the response');
    }

    const chartObjects: unknown[] = [];
    const content = jsonMatch[1];

    try {
      const fullJson = JSON.parse(content);
      if (Array.isArray(fullJson)) {
        chartObjects.push(...fullJson);
      } else if (fullJson.charts && Array.isArray(fullJson.charts)) {
        chartObjects.push(...fullJson.charts);
      } else if (typeof fullJson === 'object' && fullJson.chartType) {
        chartObjects.push(fullJson);
      }
    } catch (parseError) {
      const chartMatches = content.match(/\{(?:[^{}]|(?:\{(?:[^{}]|(?:\{[^{}]*\}))*\}))*\}/g);
      if (chartMatches) {
        chartMatches.forEach((chartStr) => {
          try {
            const cleanChartStr = chartStr.replace(/,\s*(\}|\])\s*$/, '$1');
            const chartObj = JSON.parse(cleanChartStr);
            if (chartObj.chartType) {
              chartObjects.push(chartObj);
            }
          } catch (e) {
            logger.debug('Failed to parse individual chart:', { error: e instanceof Error ? e.message : String(e) });
          }
        });
      }
    }

    logger.debug('Extracted chart objects:', { count: chartObjects.length });

    return chartObjects.filter(isValidChart).map((chartObj) => {
      const typedObj = chartObj as Record<string, unknown>;
      return {
        chartType: typedObj.chartType as 'line' | 'bar' | 'pie' | 'multi',
        title: typeof typedObj.title === 'string' ? typedObj.title : undefined,
        chartTitle: typeof typedObj.chartTitle === 'string' ? typedObj.chartTitle : undefined,
        xAxisTitle: typeof typedObj.xAxisTitle === 'string' ? typedObj.xAxisTitle : undefined,
        yAxisTitle: typeof typedObj.yAxisTitle === 'string' ? typedObj.yAxisTitle : undefined,
        xAxis: typedObj.xAxis as ChartData['xAxis'],
        yAxis: typedObj.yAxis as ChartData['yAxis'],
        data: Array.isArray(typedObj.data) ? typedObj.data : undefined,
        series: Array.isArray(typedObj.series) ? typedObj.series : undefined
      };
    });
  } catch (error) {
    logger.error('Error parsing chart data:', error instanceof Error ? error : new Error(String(error)));
    return [];
  }
};

// Generic pie chart transformer
const createPieChartTransformer = (): NullableDataTransformer<ChartData, PieChartData> => ({
  transform: (chart: ChartData): PieChartData | null => {
    try {
      if (!chart.data || !Array.isArray(chart.data) || chart.data.length === 0) {
        logger.error('Invalid pie chart data:', { chart });
        return null;
      }

      const pieData = chart.data
        .map((item) => {
          if (!item || typeof item !== 'object') {
            logger.warn('Invalid pie chart data item:', { item });
            return null;
          }

          const title = item.title || item.name || item.label;
          const value =
            typeof item.value === 'number'
              ? item.value
              : typeof item.sev2_count === 'number'
              ? item.sev2_count
              : typeof item.percentage === 'number'
              ? item.percentage
              : null;

          if (!title || value === null) {
            logger.warn('Missing required pie chart data fields:', { item });
            return null;
          }

          return { title: title as string, value };
        })
        .filter((item): item is { title: string; value: number } => item !== null);

      if (pieData.length === 0) {
        logger.error('No valid data points for pie chart');
        return null;
      }

      const total = pieData.reduce((sum, item) => sum + item.value, 0);

      return {
        type: 'pie',
        data: pieData,
        variant: 'donut',
        ariaLabel: chart.chartTitle || chart.title || 'Chart',
        hideFilter: true,
        hideLegend: false,
        size: 'medium',
        hideTiles: true,
        innerMetricValue: Number(total.toFixed(2)).toString(),
        innerMetricDescription: 'Total',
        segmentDescription: (datum) => {
          const percentage = (datum.value / total) * 100;
          return `${datum.title}: ${datum.value} (${percentage.toFixed(1)}%)`;
        },
        i18nStrings: {
          detailsValue: 'Value',
          detailsPercentage: 'Percentage',
          filterLabel: 'Filter displayed data',
          filterPlaceholder: 'Filter data',
          filterSelectedAriaLabel: 'selected',
          legendAriaLabel: 'Legend',
          chartAriaRoleDescription: 'pie chart'
        },
        empty: <EmptyStateMessage title="No data available" subtitle="There is no data to display for this chart." />
      };
    } catch (error) {
      logger.error('Error transforming pie chart:', error instanceof Error ? error : new Error(String(error)));
      return null;
    }
  }
});

// Helper functions for extracting categories and handling different data formats
function extractCategories(xAxis: unknown): string[] {
  return Array.isArray(xAxis) ? xAxis : (xAxis as any)?.categories || [];
}

function extractSeriesAndValues(chart: ChartData, categories: string[]) {
  if (chart.yAxis && typeof chart.yAxis === 'object' && !Array.isArray(chart.yAxis)) {
    return handleObjectYAxis(chart, categories);
  } else if (Array.isArray(chart.yAxis)) {
    return handleArrayYAxis(chart, categories);
  }
  return { series: [], yValues: [] };
}

function handleObjectYAxis(chart: ChartData, categories: string[]) {
  const entries = Object.entries(chart.yAxis as Record<string, unknown>);
  if (entries.length === 0) {
    return { series: [], yValues: [] };
  }

  const series = entries.map(([seriesName, seriesData]) => ({
    title: seriesName,
    type: chart.chartType as BarLineChartType,
    data: categories.map((category, index) => ({
      x: category,
      y: Array.isArray(seriesData) ? seriesData[index] || 0 : 0
    }))
  }));

  const yValues = entries.flatMap(([_, data]) => (Array.isArray(data) ? data : []));

  return { series, yValues };
}

function handleArrayYAxis(chart: ChartData, categories: string[]) {
  const yAxisArray = chart.yAxis as Array<unknown>;

  if (yAxisArray.length > 0 && typeof yAxisArray[0] === 'object') {
    return handleObjectArrayYAxis(chart, categories, yAxisArray);
  } else {
    return handleNumericArrayYAxis(chart, categories, yAxisArray);
  }
}

function handleObjectArrayYAxis(chart: ChartData, categories: string[], yAxisArray: Array<unknown>) {
  const series = yAxisArray
    .filter(
      (item): item is { name?: string; title?: string; data?: number[]; values?: number[] } =>
        typeof item === 'object' && item !== null && ('name' in item || 'data' in item)
    )
    .map((seriesItem) => ({
      title: seriesItem.name || seriesItem.title || 'Series',
      type: chart.chartType as BarLineChartType,
      data: categories.map((category, index) => ({
        x: category,
        y: (seriesItem.data || seriesItem.values || [])[index] || 0
      }))
    }));

  const yValues = yAxisArray
    .filter(
      (item): item is { data?: number[]; values?: number[] } => typeof item === 'object' && item !== null && ('data' in item || 'values' in item)
    )
    .flatMap((item) => item.data || item.values || []);

  return { series, yValues };
}

function handleNumericArrayYAxis(chart: ChartData, categories: string[], yAxisArray: Array<unknown>) {
  const numericArray = yAxisArray.filter((val): val is number => typeof val === 'number');

  const series = [
    {
      title: chart.yAxisTitle || 'Value',
      type: chart.chartType as BarLineChartType,
      data: categories.map((category, index) => ({
        x: category,
        y: numericArray[index] || 0
      }))
    }
  ];

  return { series, yValues: numericArray };
}

function createChartProps(chart: ChartData, categories: string[], series: any[], yValues: number[]): BarLineChartProps {
  const maxY = yValues.length > 0 ? Math.max(...yValues.filter((v) => !isNaN(v))) : 0;
  const yDomainMax = maxY + maxY * 0.1;

  const xTitle = chart.xAxisTitle || (typeof chart.xAxis === 'object' && chart.xAxis && 'title' in chart.xAxis ? chart.xAxis.title : 'Month');

  const yTitle =
    chart.yAxisTitle ||
    (typeof chart.yAxis === 'object' && !Array.isArray(chart.yAxis) && 'title' in chart.yAxis
      ? chart.yAxis.title
      : chart.seriesName || 'Number of Tickets');

  return {
    series,
    xDomain: categories,
    yDomain: [0, yDomainMax],
    domainType: 'string',
    xScaleType: 'categorical',
    arialLabel: (chart.chartTitle || chart.title || 'Chart') as string,
    headerName: (chart.chartTitle || chart.title || 'Chart') as string,
    xTitle: xTitle || 'Value',
    yTitle: yTitle || 'Value',
    i18nStrings: {
      xTickFormatter: (e: string) => {
        return e.includes('-') ? e.split('-').join('\n') : e.split(' ').join('\n');
      }
    },
    hideLegend: false,
    hideFilter: true,
    stackedBars: false,
    emptyMessage: <EmptyStateMessage title="No data available" subtitle="There is no data to display for this chart." />,
    noMatchMessage: 'No matching data found',
    errorText: 'Error loading chart data'
  };
}

// Generic bar/line chart transformer with refactored helper functions
const createBarLineChartTransformer = (): NullableDataTransformer<ChartData, BarLineChartProps> => ({
  transform: (chart: ChartData): BarLineChartProps | null => {
    try {
      const categories = extractCategories(chart.xAxis);
      const { series, yValues } = extractSeriesAndValues(chart, categories);

      // Rest of the function with common logic
      return createChartProps(chart, categories, series, yValues);
    } catch (error) {
      logger.error('Error transforming bar/line chart:', error instanceof Error ? error : new Error(String(error)));
      return null;
    }
  }
});

// Export the transformer instances
export const pieChartTransformer = createPieChartTransformer();
export const barLineChartTransformer = createBarLineChartTransformer();
